import * as React from "react";
import { Icon, IconInput } from "./Icon";

/**
 * A tab strip driven by a data array, the same shape as shell/navigation.ts.
 *
 * Used where several small related screens belong on one page - the configuration vocabularies, the
 * three enrollment kinds - rather than as separate sidebar entries that would bury the nav.
 *
 * State lives with the caller so the selected tab can come from the URL where that matters.
 */

export interface TabDefinition {
    key: string;
    label: string;
    icon?: IconInput;
    /** Hidden when false - use for permission gating. */
    visible?: boolean;
}

export interface TabsProps {
    tabs: TabDefinition[];
    active: string;
    onChange: (key: string) => void;
    className?: string;
}

export const Tabs: React.FC<TabsProps> = ({ tabs, active, onChange, className = "" }) => {
    const visible = tabs.filter((t) => t.visible !== false);

    return (
        <div className={`mb-6 flex flex-wrap gap-1 border-b border-border ${className}`} role="tablist">
            {visible.map((tab) => {
                const isActive = tab.key === active;
                return (
                    <button
                        key={tab.key}
                        type="button"
                        role="tab"
                        aria-selected={isActive}
                        onClick={() => onChange(tab.key)}
                        className={`-mb-px inline-flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${
                            isActive
                                ? "border-primary text-primary-text"
                                : "border-transparent text-text-weak hover:border-border-strong hover:text-text"
                        }`}
                    >
                        {tab.icon && <Icon icon={tab.icon} />}
                        {tab.label}
                    </button>
                );
            })}
        </div>
    );
};

export default Tabs;
